// frontend/src/components/features/CurateSimplified.jsx
// Simplified Curate component with button-driven interface only

import React, { useState, useEffect, useCallback, useImperativeHandle, useRef, useMemo } from "react";
import { authService } from "../../services/auth";
import { API_BASE } from "../../config/api";
import { httpRequest } from "../../utils/httpClient";
import { useBulkOnlineAvailability } from "../../hooks/useBulkOnlineAvailability";

import { GameTile } from "./CurateGameTile";
import CompareModal from "./CompareModal";
import GamePreviewModal from "./GamePreviewModal";
import LetsPlayModal from "./LetsPlayModal";
import SidebarShell from "../common/SidebarShell";
import { useLetsPlayModal } from "../../hooks/useLetsPlayModal";

import { useSidebarZIndex } from "../../hooks/useSidebarZIndex";
import logger from '../../utils/logger';

// Constants for Curate modes
const CURATE_MODES = {
  SIMILARITY: 'SIMILARITY',
  COMPARISON: 'COMPARISON',
  CROWD: 'CROWD',
};

const CurateSimplified = React.forwardRef(function CurateSimplified({
  user,
  onClose,
  isOpen = true,
  initialMode = null,
  baseGame = null,
  comparisonGames = null,
  getGameOwnershipStatus
}, ref) {
  // Dynamic z-index management for sidebar
  const { zIndex } = useSidebarZIndex('curate');

  // Refs to avoid TDZ with useImperativeHandle (functions defined later)
  const fetchSimilarGamesRef = useRef(null);
  const fetchComparisonRef = useRef(null);

  // Core state
  const [activeMode, setActiveMode] = useState(initialMode || CURATE_MODES.SIMILARITY);
  const [similarGames, setSimilarGames] = useState([]);
  const [crowdGames, setCrowdGames] = useState([]);
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(false);
  const [crowdLoading, setCrowdLoading] = useState(false);
  const [showCompareModal, setShowCompareModal] = useState(false);

  // Recalibration slider: 0 = full theme, 0.5 = balanced, 1 = full mechanics
  const [sliderWeight, setSliderWeight] = useState(0.5);
  // Pending weight tracks the slider position before Go is pressed
  const [pendingWeight, setPendingWeight] = useState(0.5);

  // Ref to access latest sliderWeight in useImperativeHandle without dependency
  const sliderWeightRef = useRef(sliderWeight);
  useEffect(() => {
    sliderWeightRef.current = sliderWeight;
  }, [sliderWeight]);

  // Similarity scope: 'all' = global results, 'library' = user's owned collection only
  const [similarityScope, setSimilarityScope] = useState('all');
  const similarityScopeRef = useRef('all');
  useEffect(() => {
    similarityScopeRef.current = similarityScope;
  }, [similarityScope]);

  // User-suggested similar games state
  const [userSuggestions, setUserSuggestions] = useState([]);
  const [suggestionSearch, setSuggestionSearch] = useState('');
  const [suggestionResults, setSuggestionResults] = useState([]);
  const [suggestionLoading, setSuggestionLoading] = useState(false);
  const [submittingSuggestion, setSubmittingSuggestion] = useState(false);
  const suggestionSearchTimer = useRef(null);

  // Base game for similarity/comparison
  const [currentBaseGame, setCurrentBaseGame] = useState(baseGame);

  // Game preview state
  const [previewGame, setPreviewGame] = useState(null);

  // Let's Play state for game preview's Score & Log tab
  const {
    showModal: showLetSPlayModal,
    selectedGame: letSPlayGame,
    showSessionForm: showSessionFormInLetSPlay,
    showRulesSummary: showRulesSummaryInLetSPlay,
    setShowSessionForm,
    setShowRulesSummary,
    handlers: letSPlayHandlers,
  } = useLetsPlayModal(user, () => {});
  const [showScorePadInLetSPlay, setShowScorePadInLetSPlay] = useState(false);
  const [scorePadSessionData, setScorePadSessionData] = useState(null);
  const [userMap] = useState(new Map());

  // Bulk online availability for similar games
  const gameIds = useMemo(() =>
    similarGames
      .map(game => game.game_id || game.id)
      .filter(Boolean),
    [similarGames]
  );
  const { getGameAvailability } = useBulkOnlineAvailability(gameIds);

  // Fetch crowd recommendations for the authenticated user
  const fetchCrowdRecs = useCallback(async (limit = 10) => {
    if (!user) return;
    setCrowdLoading(true);
    try {
      const res = await httpRequest(
        `${API_BASE}/recommendations/crowd?limit=${limit}`,
        {
          method: 'GET',
          headers: {
            ...authService.getAuthHeaders(),
            'Content-Type': 'application/json',
          },
        }
      );
      if (res.ok) {
        const data = await res.json();
        setCrowdGames(data.recommendations || []);
      } else {
        logger.warn('Failed to fetch crowd recommendations');
        setCrowdGames([]);
      }
    } catch (err) {
      logger.error('Error fetching crowd recommendations:', err);
      setCrowdGames([]);
    } finally {
      setCrowdLoading(false);
    }
  }, [user]);

  // Imperative methods for parent components
  useImperativeHandle(ref, () => ({
    showSimilarGames: (game) => {
      setCurrentBaseGame(game);
      setActiveMode(CURATE_MODES.SIMILARITY);
      // Support both game.id and game.game_id for compatibility
      const gameId = game.id ?? game.game_id;
      fetchSimilarGamesRef.current?.(gameId, 10, sliderWeightRef.current, similarityScopeRef.current);
    },
    showComparison: (gameA, gameB) => {
      setCurrentBaseGame(gameA);
      // Fetch comparison and open modal, but stay in current sidebar view
      // Support both .id and .game_id for compatibility
      const gameAId = gameA.id ?? gameA.game_id;
      const gameBId = gameB.id ?? gameB.game_id;
      fetchComparisonRef.current?.(gameAId, gameBId);
    },
    reset: () => {
      setActiveMode(CURATE_MODES.SIMILARITY);
      setSimilarGames([]);
      setComparison(null);
      setCurrentBaseGame(null);
      setSimilarityScope('all');
      setUserSuggestions([]);
      setSuggestionSearch('');
      setSuggestionResults([]);
    }
  }), []); // Empty deps - refs are stable, functions assigned after definition

  // Fetch similar games for a given game ID
  const fetchSimilarGames = useCallback(async (gameId, limit = 10, mechWeight = 0.5, scope = 'all') => {
    if (!gameId) return;

    const mechWeightParam = parseFloat(mechWeight.toFixed(3));
    const feelWeight = parseFloat((1 - mechWeight).toFixed(3));
    const interactionWeightParam = parseFloat((feelWeight / 2).toFixed(3));
    const moodWeightParam = parseFloat((feelWeight / 2).toFixed(3));
    const themeWeightParam = 0;
    const collectionParam = scope === 'library' ? '&collection_only=true' : '';

    setLoading(true);
    try {
      const res = await httpRequest(
        `${API_BASE}/games/${gameId}/similar?limit=${limit}&mechanics_weight=${mechWeightParam}&theme_weight=${themeWeightParam}&interaction_weight=${interactionWeightParam}&mood_weight=${moodWeightParam}${collectionParam}`,
        {
          method: "GET",
          headers: {
            ...authService.getAuthHeaders(),
            "Content-Type": "application/json",
          },
        }
      );

      if (res.ok) {
        const data = await res.json();

        // Fetch full game details for each similar game
        const similarGamesWithDetails = await Promise.all(
          (data.similar_games || []).map(async (similarGame) => {
            try {
              const gameRes = await httpRequest(
                `${API_BASE}/games/${similarGame.game_id}/details`,
                {
                  method: "GET",
                  headers: authService.getAuthHeaders(),
                }
              );

              if (gameRes.ok) {
                const gameDetails = await gameRes.json();
                // Merge similarity data with full game details
                return {
                  ...gameDetails,
                  ...similarGame, // Include similarity scores and metadata
                };
              } else {
                // Fallback to similarity data if game details fetch fails
                return similarGame;
              }
            } catch (error) {
              logger.warn(`Failed to fetch details for game ${similarGame.game_id}:`, error);
              return similarGame;
            }
          })
        );

        setSimilarGames(similarGamesWithDetails);
              } else {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.detail || "Failed to fetch similar games");
      }
    } catch (err) {
      logger.error("Error fetching similar games:", err);
      setSimilarGames([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Assign to ref so useImperativeHandle can access it
  useEffect(() => {
    fetchSimilarGamesRef.current = fetchSimilarGames;
  }, [fetchSimilarGames]);

  // Fetch user-suggested similar games
  const fetchUserSuggestions = useCallback(async (gameId) => {
    if (!gameId) return;
    try {
      const res = await httpRequest(
        `${API_BASE}/games/${gameId}/user-suggestions`,
        { method: 'GET', headers: authService.getAuthHeaders() }
      );
      if (res.ok) {
        const data = await res.json();
        setUserSuggestions(data.suggestions || []);
      }
    } catch (err) {
      logger.warn('Failed to fetch user suggestions:', err);
    }
  }, []);

  // Search games for suggestion
  const handleSuggestionSearch = useCallback((query) => {
    setSuggestionSearch(query);
    if (suggestionSearchTimer.current) clearTimeout(suggestionSearchTimer.current);
    if (!query.trim()) {
      setSuggestionResults([]);
      return;
    }
    suggestionSearchTimer.current = setTimeout(async () => {
      setSuggestionLoading(true);
      try {
        const res = await httpRequest(
          `${API_BASE}/games/search?q=${encodeURIComponent(query.trim())}&limit=5`,
          { method: 'GET', headers: authService.getAuthHeaders() }
        );
        if (res.ok) {
          const data = await res.json();
          setSuggestionResults(data.results || data.games || []);
        }
      } catch (err) {
        logger.warn('Suggestion search failed:', err);
      } finally {
        setSuggestionLoading(false);
      }
    }, 300);
  }, []);

  // Submit a user suggestion
  const handleSubmitSuggestion = useCallback(async (suggestedGameId) => {
    if (!currentBaseGame || !user) return;
    const baseGameId = Number(currentBaseGame.id ?? currentBaseGame.game_id);
    const targetGameId = Number(suggestedGameId);
    if (!baseGameId || !targetGameId) return;

    setSubmittingSuggestion(true);
    try {
      const res = await httpRequest(
        `${API_BASE}/games/suggest-similar`,
        {
          method: 'POST',
          headers: authService.getAuthHeaders(),
          body: JSON.stringify({ base_game_id: baseGameId, suggested_game_id: targetGameId })
        }
      );
      if (res.ok) {
        setSuggestionSearch('');
        setSuggestionResults([]);
        fetchUserSuggestions(baseGameId);
      } else {
        logger.warn('Suggestion submission failed:', res.status);
      }
    } catch (err) {
      logger.error('Failed to submit suggestion:', err);
    } finally {
      setSubmittingSuggestion(false);
    }
  }, [currentBaseGame, user, fetchUserSuggestions]);

  // Remove a user's own suggestion
  const handleRemoveSuggestion = useCallback(async (suggestedGameId) => {
    if (!currentBaseGame || !user) return;
    const baseGameId = Number(currentBaseGame.id ?? currentBaseGame.game_id);
    const targetGameId = Number(suggestedGameId);
    if (!baseGameId || !targetGameId) return;

    try {
      const res = await httpRequest(
        `${API_BASE}/games/suggest-similar`,
        {
          method: 'DELETE',
          headers: authService.getAuthHeaders(),
          body: JSON.stringify({ base_game_id: baseGameId, suggested_game_id: targetGameId })
        }
      );
      if (res.ok) {
        fetchUserSuggestions(baseGameId);
      } else {
        logger.warn('Suggestion removal failed:', res.status);
      }
    } catch (err) {
      logger.error('Failed to remove suggestion:', err);
    }
  }, [currentBaseGame, user, fetchUserSuggestions]);

  // Fetch user suggestions when base game or similar games change
  useEffect(() => {
    if (currentBaseGame && activeMode === CURATE_MODES.SIMILARITY) {
      const gameId = currentBaseGame.id ?? currentBaseGame.game_id;
      fetchUserSuggestions(gameId);
    }
  }, [currentBaseGame, activeMode, fetchUserSuggestions]);

  // Fetch comparison between two games
  const fetchComparison = useCallback(async (gameAId, gameBId, tileData = null) => {
    if (!gameAId || !gameBId) return;

    setLoading(true);
    try {
      const res = await httpRequest(
        `${API_BASE}/games/compare?a=${gameAId}&b=${gameBId}`,
        {
          method: "GET",
          headers: {
            ...authService.getAuthHeaders(),
            "Content-Type": "application/json",
          },
        }
      );

      if (res.ok) {
        const data = await res.json();
        const comparison = { ...data.comparison };
        // Use the exact final_score from the sidebar tile when available,
        // so the Compare Modal percentage matches the tile for the same pair.
        if (tileData?.final_score !== undefined && tileData?.final_score !== null) {
          comparison.final_score = tileData.final_score;
        }
        setComparison(comparison);
        setShowCompareModal(true);
              } else {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.detail || "Failed to compare games");
      }
    } catch (err) {
      logger.error("Error comparing games:", err);
      setComparison(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Assign to ref so useImperativeHandle can access it
  useEffect(() => {
    fetchComparisonRef.current = fetchComparison;
  }, [fetchComparison]);

  // Handle compare button click on similar game tiles
  const handleCompareGame = useCallback((similarGame) => {
    if (!currentBaseGame) return;

    // Fetch comparison and open modal, but stay in similarity view
    // Support both .id and .game_id for compatibility
    const baseGameId = currentBaseGame.id ?? currentBaseGame.game_id;
    const similarGameId = similarGame.id ?? similarGame.game_id;
    fetchComparison(baseGameId, similarGameId, similarGame);
  }, [currentBaseGame, fetchComparison]);

  // Handle back to similarity results
  const handleBackToSimilarity = useCallback(() => {
    if (currentBaseGame) {
      setActiveMode(CURATE_MODES.SIMILARITY);
      setComparison(null);
    }
  }, [currentBaseGame]);

  // Handle game preview - fetch full game details
  const handlePreviewGame = useCallback(async (game) => {
    if (!game) return;
    // Support both game.id and game.game_id for compatibility
    const gameId = game.id ?? game.game_id;
    if (!gameId) return;

    try {
      const res = await httpRequest(`${API_BASE}/games/${gameId}/details`, {
        method: "GET",
        headers: {
          ...authService.getAuthHeaders(),
        },
      });
      if (res.ok) {
        const gameDetails = await res.json();
        setPreviewGame(gameDetails);
      } else {
        setPreviewGame(game);
      }
    } catch (err) {
      logger.error("Failed to fetch game details:", err);
      setPreviewGame(game);
    }
  }, [setPreviewGame]);

  // Initialize with base game if provided
  useEffect(() => {
    if (baseGame && initialMode) {
      setCurrentBaseGame(baseGame);
      setActiveMode(initialMode);

      if (initialMode === CURATE_MODES.SIMILARITY) {
        fetchSimilarGames(baseGame.id, 10, sliderWeight, similarityScope);
      } else if (initialMode === CURATE_MODES.COMPARISON && comparisonGames) {
        fetchComparison(baseGame.id, comparisonGames[0]?.id);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseGame, initialMode, comparisonGames]);

  // Re-fetch when slider changes (only if a base game is already loaded)
  useEffect(() => {
    if (currentBaseGame && activeMode === CURATE_MODES.SIMILARITY) {
      fetchSimilarGames(currentBaseGame.id, 10, sliderWeight, similarityScope);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sliderWeight]);

  // Fetch crowd recs when switching to CROWD tab
  useEffect(() => {
    if (activeMode === CURATE_MODES.CROWD && crowdGames.length === 0 && !crowdLoading) {
      fetchCrowdRecs();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeMode]);

  // Render similarity results
  const renderSimilarityResults = () => {
    if (loading) {
      return (
        <div className="curate-loading" style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              minHeight: '200px'
            }}>
          <div className="loading-spinner" style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <div style={{
                width: '64px',
                height: '64px',
                border: '3px solid #f3f3f3',
                borderTop: '3px solid var(--primary-color, #1976d2)',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <img
                  src="/favicon-192x192.png"
                  alt="Loading"
                  style={{
                    width: '30px',
                    height: '30px',
                    borderRadius: '50%',
                    objectFit: 'cover',
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)'
                  }}
                />
              </div>
            </div>
        </div>
      );
    }

    if (!currentBaseGame) {
      return (
        <div className="curate-empty">
          <p>No game selected for similarity search.</p>
        </div>
      );
    }

    if (similarGames.length === 0 && !loading) {
      return (
        <div className="curate-similarity-results" style={{ width: '100%' }}>
          <div className="curate-header" style={{ padding: '1rem 1rem 1rem 1rem', marginBottom: '0.5rem', marginLeft: '1rem' }}>
            <h3>Games Similar to &quot;{currentBaseGame.name}&quot;</h3>
          </div>
          {/* Scope switch: keep visible so user can toggle back */}
          <div style={{ margin: '0 1rem 0.5rem 1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div
              role="group"
              aria-label="Similarity scope"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                border: '1px solid var(--border-color, #ddd)',
                borderRadius: '16px',
                backgroundColor: 'var(--bg-primary, white)',
                padding: '1px',
              }}
            >
              <button
                type="button"
                data-testid="scope-switch-all"
                aria-pressed={similarityScope === 'all'}
                onClick={() => {
                  if (similarityScope === 'all') return;
                  setSimilarityScope('all');
                  const gameId = currentBaseGame.id ?? currentBaseGame.game_id;
                  fetchSimilarGames(gameId, 10, sliderWeight, 'all');
                }}
                style={{
                  padding: '0.25rem 0.75rem',
                  borderRadius: '14px',
                  border: 'none',
                  backgroundColor: similarityScope === 'all' ? 'var(--primary-color, #1976d2)' : 'transparent',
                  color: similarityScope === 'all' ? 'white' : 'var(--text-primary, #333)',
                  fontSize: '0.75rem',
                  fontWeight: similarityScope === 'all' ? '500' : '400',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  whiteSpace: 'nowrap',
                }}
              >
                All
              </button>
              <button
                type="button"
                data-testid="scope-switch-library"
                aria-pressed={similarityScope === 'library'}
                disabled={!user}
                title={!user ? 'Sign in to search your library' : 'Show similar games from your library'}
                onClick={() => {
                  if (!user || similarityScope === 'library') return;
                  setSimilarityScope('library');
                  const gameId = currentBaseGame.id ?? currentBaseGame.game_id;
                  fetchSimilarGames(gameId, 10, sliderWeight, 'library');
                }}
                style={{
                  padding: '0.25rem 0.75rem',
                  borderRadius: '14px',
                  border: 'none',
                  backgroundColor: similarityScope === 'library' ? 'var(--primary-color, #1976d2)' : 'transparent',
                  color: similarityScope === 'library' ? 'white' : 'var(--text-primary, #333)',
                  fontSize: '0.75rem',
                  fontWeight: similarityScope === 'library' ? '500' : '400',
                  cursor: !user ? 'not-allowed' : 'pointer',
                  opacity: !user ? 0.45 : 1,
                  transition: 'all 0.2s ease',
                  whiteSpace: 'nowrap',
                }}
              >
                Library
              </button>
            </div>
          </div>
          <div className="curate-empty">
            <p>No similar games found{similarityScope === 'library' ? ' in your library' : ''}.</p>
          </div>
        </div>
      );
    }

    const sliderLabel =
      pendingWeight < 0.35
        ? "Feel-focused"
        : pendingWeight > 0.65
        ? "Mechanics-focused"
        : "Balanced";

    return (
      <div className="curate-similarity-results" style={{ width: '100%' }}>
        <div className="curate-header" style={{ padding: '1rem 1rem 1rem 1rem', marginBottom: '0.5rem', marginLeft: '1rem' }}>
          <h3>Games Similar to &quot;{currentBaseGame.name}&quot;</h3>
          <p className="curate-subtitle">Found {similarGames.length} similar games</p>
        </div>

        {/* Scope switch: All vs Library — pill/segmented switch */}
        <div style={{ margin: '0 1rem 0.5rem 1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div
            role="group"
            aria-label="Similarity scope"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              border: '1px solid var(--border-color, #ddd)',
              borderRadius: '16px',
              backgroundColor: 'var(--bg-primary, white)',
              padding: '1px',
            }}
          >
            <button
              type="button"
              data-testid="scope-switch-all"
              aria-pressed={similarityScope === 'all'}
              onClick={() => {
                if (similarityScope === 'all') return;
                setSimilarityScope('all');
                const gameId = currentBaseGame.id ?? currentBaseGame.game_id;
                fetchSimilarGames(gameId, 10, sliderWeight, 'all');
              }}
              style={{
                padding: '0.25rem 0.75rem',
                borderRadius: '14px',
                border: 'none',
                backgroundColor: similarityScope === 'all' ? 'var(--primary-color, #1976d2)' : 'transparent',
                color: similarityScope === 'all' ? 'white' : 'var(--text-primary, #333)',
                fontSize: '0.75rem',
                fontWeight: similarityScope === 'all' ? '500' : '400',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                whiteSpace: 'nowrap',
              }}
            >
              All
            </button>
            <button
              type="button"
              data-testid="scope-switch-library"
              aria-pressed={similarityScope === 'library'}
              disabled={!user}
              title={!user ? 'Sign in to search your library' : 'Show similar games from your library'}
              onClick={() => {
                if (!user || similarityScope === 'library') return;
                setSimilarityScope('library');
                const gameId = currentBaseGame.id ?? currentBaseGame.game_id;
                fetchSimilarGames(gameId, 10, sliderWeight, 'library');
              }}
              style={{
                padding: '0.25rem 0.75rem',
                borderRadius: '14px',
                border: 'none',
                backgroundColor: similarityScope === 'library' ? 'var(--primary-color, #1976d2)' : 'transparent',
                color: similarityScope === 'library' ? 'white' : 'var(--text-primary, #333)',
                fontSize: '0.75rem',
                fontWeight: similarityScope === 'library' ? '500' : '400',
                cursor: !user ? 'not-allowed' : 'pointer',
                opacity: !user ? 0.45 : 1,
                transition: 'all 0.2s ease',
                whiteSpace: 'nowrap',
              }}
            >
              Library
            </button>
          </div>
        </div>

        <div className="curate-slider-row" data-testid="recalibration-slider" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', margin: '0 1rem 1rem 1rem' }}>
          <div className="curate-slider-controls" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', width: '100%' }}>
            <span className="curate-slider-label curate-slider-label--left" title="Prioritise interaction scale and mood (feel)">� Feel</span>
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={pendingWeight}
              onChange={(e) => setPendingWeight(parseFloat(e.target.value))}
              className="curate-slider"
              aria-label="Recalibration slider: feel vs mechanics weight"
              title={`${sliderLabel} (${Math.round(pendingWeight * 100)}% mechanics)`}
              style={{ flex: 1 }}
            />
            <span className="curate-slider-label curate-slider-label--right" title="Prioritise shared mechanics and gameplay loops">⚙️ Mechanics</span>
            <button
              type="button"
              className={`curate-slider-go${pendingWeight === sliderWeight ? ' curate-slider-go--hidden' : ''}`}
              onClick={() => {
                setSliderWeight(pendingWeight);
                fetchSimilarGames(currentBaseGame.game_id || currentBaseGame.id, 10, pendingWeight);
              }}
              aria-label="Recalculate with new balance"
              title="Recalculate results with new balance"
              disabled={pendingWeight === sliderWeight}
            >
              Go
            </button>
          </div>
          <div className="curate-slider-caption">{sliderLabel}</div>
        </div>

        <div className="curate-games-grid" style={{ width: '100% !important', padding: '0 16px', boxSizing: 'border-box' }}>
          {similarGames.map((game, index) => (
            <div key={game.game_id || game.id || `game-${index}`} style={{ width: '100% !important', marginBottom: '12px', marginLeft: '1rem' }}>
              <GameTile
                game={game}
                user={user}
                getGameAvailability={getGameAvailability}
                onCompare={() => handleCompareGame(game)}
                hideMarketplaceButton={false}
                hideSimilarity={false}
                gameChips={[currentBaseGame]}
                onAddToSearch={null}
                setPreviewGame={handlePreviewGame}
                hideDescription={true}
                onLetSPlay={null}
                onSearchSimilar={null}
                onAddToMarketplace={null}
                onAddReminder={null}
                onCompareGames={null}
                getGameOwnershipStatus={getGameOwnershipStatus}
                showAddToCollection={true}
              />
            </div>
          ))}
        </div>

        {/* User-suggested similar games */}
        <div style={{ padding: '0.75rem 1rem 1rem 1rem', marginLeft: '1rem', borderTop: '1px solid var(--border-color, #eee)', marginTop: '0.5rem' }}>
          <h4 style={{ fontSize: '0.85rem', fontWeight: 600, margin: '0 0 0.5rem 0', color: 'var(--text-primary, #333)' }}>
            Community Suggestions
          </h4>

          {/* Suggestion search */}
          {user ? (
            <div style={{ position: 'relative', marginBottom: '0.75rem' }}>
              <input
                type="text"
                value={suggestionSearch}
                onChange={(e) => handleSuggestionSearch(e.target.value)}
                placeholder="Search for a game to suggest as similar..."
                style={{
                  width: '100%',
                  padding: '0.5rem 0.75rem',
                  borderRadius: '6px',
                  border: '1px solid var(--border-color, #ccc)',
                  fontSize: '0.8rem',
                  fontFamily: 'inherit',
                  backgroundColor: 'var(--bg-primary, white)',
                  color: 'var(--text-primary, #333)',
                  boxSizing: 'border-box'
                }}
              />
              {suggestionLoading && (
                <div style={{ position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)', fontSize: '0.7rem', color: 'var(--text-secondary, #999)' }}>
                  ...
                </div>
              )}
              {suggestionResults.length > 0 && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  backgroundColor: 'var(--bg-primary, white)',
                  border: '1px solid var(--border-color, #ddd)',
                  borderRadius: '6px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  zIndex: 10,
                  maxHeight: '200px',
                  overflowY: 'auto'
                }}>
                  {suggestionResults.map((g) => {
                    const gid = g.id || g.game_id;
                    const baseId = currentBaseGame.id ?? currentBaseGame.game_id;
                    const alreadyInResults = similarGames.some(sg => String(sg.game_id || sg.id) === String(gid));
                    const alreadySuggested = userSuggestions.some(us => String(us.game_id) === String(gid));
                    const isBaseGame = String(gid) === String(baseId);
                    return (
                      <button
                        key={gid}
                        onClick={() => handleSubmitSuggestion(gid)}
                        disabled={submittingSuggestion || isBaseGame || alreadyInResults || alreadySuggested}
                        style={{
                          display: 'block',
                          width: '100%',
                          padding: '0.5rem 0.75rem',
                          border: 'none',
                          background: 'transparent',
                          color: 'var(--text-primary, #333)',
                          fontSize: '0.8rem',
                          cursor: (submittingSuggestion || isBaseGame || alreadyInResults || alreadySuggested) ? 'not-allowed' : 'pointer',
                          textAlign: 'left',
                          opacity: (isBaseGame || alreadyInResults || alreadySuggested) ? 0.5 : 1,
                          borderBottom: '1px solid var(--border-color, #f0f0f0)'
                        }}
                        onMouseOver={(e) => { if (!e.target.disabled) e.target.style.backgroundColor = 'var(--bg-secondary, #f5f5f5)'; }}
                        onMouseOut={(e) => e.target.style.backgroundColor = 'transparent'}
                      >
                        {g.name || g.game_name}
                        {isBaseGame && ' (this game)'}
                        {alreadyInResults && ' (already in results)'}
                        {alreadySuggested && ' (already suggested)'}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary, #999)', margin: '0 0 0.5rem 0' }}>
              Sign in to suggest a similar game.
            </p>
          )}

          {/* Display user suggestions */}
          {userSuggestions.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
              {userSuggestions.map((s) => (
                <div key={s.game_id} style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '0.4rem 0.6rem',
                  borderRadius: '6px',
                  backgroundColor: 'var(--bg-secondary, #f9f9f9)',
                  border: '1px solid var(--border-color, #eee)'
                }}>
                  <span style={{ fontSize: '0.8rem', color: 'var(--text-primary, #333)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {s.name}
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexShrink: 0, marginLeft: '0.5rem' }}>
                    <span style={{
                      fontSize: '0.65rem',
                      padding: '0.1rem 0.4rem',
                      borderRadius: '10px',
                      backgroundColor: 'var(--primary-color, #1976d2)',
                      color: 'white',
                      fontWeight: 500
                    }}>
                      {s.vote_count} {s.vote_count === 1 ? 'vote' : 'votes'}
                    </span>
                    {s.is_suggested_by_user && (
                      <button
                        onClick={() => handleRemoveSuggestion(s.game_id)}
                        aria-label={`Remove your suggestion for ${s.name}`}
                        title="Remove my suggestion"
                        style={{
                          border: 'none',
                          background: 'transparent',
                          color: 'var(--text-secondary, #999)',
                          cursor: 'pointer',
                          fontSize: '0.9rem',
                          padding: '0 0.2rem',
                          lineHeight: 1
                        }}
                      >
                        ×
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Render comparison results - now just shows button to reopen modal
  const renderComparisonResults = () => {
    if (loading) {
      return (
        <div className="curate-loading" style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              minHeight: '200px'
            }}>
          <div className="loading-spinner" style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <div style={{
                width: '64px',
                height: '64px',
                border: '3px solid #f3f3f3',
                borderTop: '3px solid var(--primary-color, #1976d2)',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <img
                  src="/favicon-192x192.png"
                  alt="Loading"
                  style={{
                    width: '30px',
                    height: '30px',
                    borderRadius: '50%',
                    objectFit: 'cover',
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)'
                  }}
                />
              </div>
            </div>
        </div>
      );
    }

    return (
      <div className="curate-comparison-results">
        <div className="curate-header" style={{ padding: '1rem 1rem 1rem 1rem', marginBottom: '1rem', marginLeft: '1rem' }}>
          <button
            className="curate-back-button"
            onClick={handleBackToSimilarity}
          >
            ← Back to Similar Games
          </button>
        </div>

        {comparison && (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <p style={{ marginBottom: '16px', color: 'var(--text-secondary, #666)' }}>
              Viewing comparison results in modal
            </p>
            <button
              onClick={() => setShowCompareModal(true)}
              style={{
                padding: '12px 24px',
                backgroundColor: 'var(--primary-color, #1976d2)',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
              }}
            >
              Show Comparison
            </button>
          </div>
        )}
      </div>
    );
  };

  // Render crowd recommendations panel
  const renderCrowdResults = () => {
    if (!user) {
      return (
        <div className="curate-empty">
          <p>Sign in to see games recommended by users with similar taste.</p>
        </div>
      );
    }

    if (crowdLoading) {
      return (
        <div className="curate-loading" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '200px' }}>
          <div style={{ width: '48px', height: '48px', border: '3px solid #f3f3f3', borderTop: '3px solid var(--primary-color, #1976d2)', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
        </div>
      );
    }

    if (crowdGames.length === 0) {
      return (
        <div className="curate-empty">
          <p>No crowd recommendations yet — add more games to your collection to unlock this.</p>
          <button className="curate-crowd-refresh" onClick={() => fetchCrowdRecs()}>Refresh</button>
        </div>
      );
    }

    return (
      <div className="curate-crowd-results">
        <div className="curate-header" style={{ padding: '1rem 1rem 0.5rem 1rem', marginLeft: '1rem' }}>
          <h3>Games Your Crowd Recommends</h3>
          <p className="curate-subtitle">{crowdGames.length} games from users with similar taste</p>
        </div>
        <div className="curate-games-grid" style={{ padding: '0 16px' }}>
          {crowdGames.map((game, index) => (
            <div key={game.game_id || `crowd-${index}`} style={{ marginBottom: '12px', marginLeft: '1rem' }}>
              <GameTile
                game={game}
                user={user}
                getGameAvailability={getGameAvailability}
                onCompare={null}
                hideMarketplaceButton={false}
                hideSimilarity={true}
                gameChips={[]}
                onAddToSearch={null}
                setPreviewGame={handlePreviewGame}
                hideDescription={true}
                onLetSPlay={null}
                onSearchSimilar={null}
                onAddToMarketplace={null}
                onAddReminder={null}
                onCompareGames={null}
                getGameOwnershipStatus={getGameOwnershipStatus}
                showAddToCollection={true}
              />
            </div>
          ))}
        </div>
        <div style={{ padding: '0.5rem 1rem 1rem 2rem' }}>
          <button className="curate-crowd-refresh" onClick={() => fetchCrowdRecs()}>Refresh</button>
        </div>
      </div>
    );
  };

  // Main content based on active mode
  const renderContent = () => {
    switch (activeMode) {
      case CURATE_MODES.SIMILARITY:
        return renderSimilarityResults();
      case CURATE_MODES.COMPARISON:
        return renderComparisonResults();
      case CURATE_MODES.CROWD:
        return renderCrowdResults();
      default:
        return renderSimilarityResults();
    }
  };

  // Let’s Play handlers for the preview modal’s Score & Log tab
  const handleLogSession = useCallback((game) => {
    if (!game) return;
    letSPlayHandlers.openModal(game);
    setShowScorePadInLetSPlay(false);
    setShowRulesSummary(false);
    setShowSessionForm(true);
  }, [letSPlayHandlers, setShowSessionForm, setShowRulesSummary]);

  const handleOpenScorePad = useCallback((game) => {
    if (!game) return;
    letSPlayHandlers.openModal(game);
    setShowSessionForm(false);
    setShowRulesSummary(false);
    setShowScorePadInLetSPlay(true);
  }, [letSPlayHandlers, setShowSessionForm, setShowRulesSummary]);

  const handleCloseScorePad = useCallback(() => {
    setShowScorePadInLetSPlay(false);
  }, []);

  const handleLogSessionFromScorePad = useCallback((data) => {
    setScorePadSessionData(data);
    setShowScorePadInLetSPlay(false);
    setShowSessionForm(true);
  }, [setShowSessionForm]);

  const handleCloseLetSPlay = useCallback(() => {
    setShowScorePadInLetSPlay(false);
    setScorePadSessionData(null);
    letSPlayHandlers.closeModal();
  }, [letSPlayHandlers]);

  const handleSaveSession = useCallback((result) => {
    setShowScorePadInLetSPlay(false);
    setScorePadSessionData(null);
    letSPlayHandlers.handleSessionLogged(result);
    setPreviewGame(null);
  }, [letSPlayHandlers]);

  const fetchUserInfo = useCallback(async (userId) => `User ${userId}`, []);

  return (
    <>
      <SidebarShell
        isOpen={isOpen}
        onClose={onClose}
        title={
          <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            Game Discovery
            <span style={{ fontSize: '0.65em', padding: '0.15em 0.4em', borderRadius: '4px', background: 'var(--primary-color, #1976d2)', color: 'white', fontWeight: 500, verticalAlign: 'middle' }}>beta</span>
          </span>
        }
        zIndex={zIndex}
        showCloseButton={activeMode === CURATE_MODES.COMPARISON && !previewGame}
      >
        <div className="curate-simplified" style={{ width: '100%', height: '100%', position: 'relative' }}>
          {/* Mode tab bar — only show Similarity and Crowd (Comparison is modal-driven) */}
          {activeMode !== CURATE_MODES.COMPARISON && (
            <div className="curate-tab-bar curate-tab-bar--sticky" role="tablist">
              <div className="curate-tab-bar__tabs">
                <button
                  role="tab"
                  aria-selected={activeMode === CURATE_MODES.SIMILARITY}
                  className={`curate-tab${activeMode === CURATE_MODES.SIMILARITY ? ' curate-tab--active' : ''}`}
                  onClick={() => setActiveMode(CURATE_MODES.SIMILARITY)}
                >
                  Similar Games
                </button>
                {user && (
                  <button
                    role="tab"
                    aria-selected={activeMode === CURATE_MODES.CROWD}
                    className={`curate-tab${activeMode === CURATE_MODES.CROWD ? ' curate-tab--active' : ''}`}
                    onClick={() => setActiveMode(CURATE_MODES.CROWD)}
                  >
                    Crowd Picks
                  </button>
                )}
              </div>
              <button
                type="button"
                className="curate-tab-bar__close"
                onClick={onClose}
                aria-label="Close Game Discovery"
                title="Close Game Discovery"
              >
                ×
              </button>
            </div>
          )}
          {renderContent()}
        </div>
      </SidebarShell>

      <CompareModal
        isOpen={showCompareModal}
        onClose={() => setShowCompareModal(false)}
        comparison={comparison}
      />

      <GamePreviewModal
        game={previewGame}
        onClose={() => setPreviewGame(null)}
        user={user}
        hideActions={true}
        letsPlayConfig={{
          onLogSession: handleLogSession,
          onOpenScorePad: handleOpenScorePad,
        }}
      />

      <LetsPlayModal
        isOpen={showLetSPlayModal}
        game={letSPlayGame}
        user={user}
        onClose={handleCloseLetSPlay}
        showSessionForm={showSessionFormInLetSPlay}
        showRulesSummary={showRulesSummaryInLetSPlay}
        showScorePad={showScorePadInLetSPlay}
        onLogSession={letSPlayHandlers.logSession}
        onOpenScorePad={handleOpenScorePad}
        onCloseScorePad={handleCloseScorePad}
        onLogSessionFromScorePad={handleLogSessionFromScorePad}
        scorePadSessionData={scorePadSessionData}
        onSaveSession={handleSaveSession}
        userMap={userMap}
        fetchUserInfo={fetchUserInfo}
        source="curate"
      />
    </>
  );
});

export default CurateSimplified;
